import { useState } from 'react'
import { Copy, Check, ExternalLink, Filter } from 'lucide-react'
import { examples } from '@/data/reportContent'
import Button from '@/components/Button'

const Examples = () => {
  const [selectedDifficulty, setSelectedDifficulty] = useState('All')
  const [selectedTech, setSelectedTech] = useState('All')
  const [copiedPrompt, setCopiedPrompt] = useState('')

  const copyPrompt = async (prompt) => {
    try {
      await navigator.clipboard.writeText(prompt)
      setCopiedPrompt(prompt)
      setTimeout(() => setCopiedPrompt(''), 2000)
    } catch (err) {
      console.error('Failed to copy prompt:', err)
    }
  }

  const difficulties = ['All', 'Beginner', 'Intermediate', 'Advanced']
  const technologies = ['All', ...new Set(examples.flatMap(example => example.tags))]

  const filteredExamples = examples.filter(example => {
    const difficultyMatch = selectedDifficulty === 'All' || example.difficulty === selectedDifficulty
    const techMatch = selectedTech === 'All' || example.tags.includes(selectedTech)
    return difficultyMatch && techMatch
  })

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'Intermediate':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
      case 'Advanced':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Project Examples
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Real-world examples and prompts to help you get started with different types of projects. 
            Copy the prompts and use them directly with GitHub Copilot Agent.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <Filter className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <span className="font-semibold text-gray-900 dark:text-white">Filter Examples</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Difficulty Level
              </label>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {difficulties.map(difficulty => (
                  <option key={difficulty} value={difficulty}>
                    {difficulty}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Technology
              </label>
              <select
                value={selectedTech}
                onChange={(e) => setSelectedTech(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {technologies.map(tech => (
                  <option key={tech} value={tech}>
                    {tech}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Examples Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {filteredExamples.map((example, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-shadow"
            >
              {/* Card Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {example.title}
                  </h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(example.difficulty)}`}>
                    {example.difficulty}
                  </span>
                </div>
                
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {example.description}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {example.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-primary-200 dark:bg-primary-800 text-primary-800 dark:text-primary-200 rounded-full text-sm font-medium border border-primary-300 dark:border-primary-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Prompt Section */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    Agent Prompt
                  </h4>
                  <button
                    onClick={() => copyPrompt(example.prompt)}
                    className="flex items-center space-x-1 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  >
                    {copiedPrompt === example.prompt ? (
                      <>
                        <Check className="w-4 h-4" />
                        <span className="text-xs">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span className="text-xs">Copy</span>
                      </>
                    )}
                  </button>
                </div>
                
                <div className="bg-gray-900 rounded-lg p-4 overflow-hidden">
                  <pre className="text-green-400 text-sm whitespace-pre-wrap">
                    {example.prompt}
                  </pre>
                </div>
              </div>

              {/* Action Section */}
              <div className="px-6 pb-6">
                <div className="flex space-x-3">
                  <Button
                    onClick={() => copyPrompt(example.prompt)}
                    variant="primary"
                    className="flex-1 font-medium"
                  >
                    Copy & Use Prompt
                  </Button>
                  <a
                    href="/guide"
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium flex items-center"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Guide
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredExamples.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No examples found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your filters to see more examples.
            </p>
          </div>
        )}

        {/* Tips Section */}
        <div className="mt-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-4">💡 Pro Tips for Using These Examples</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-2">Customize the Prompts</h4>
              <p className="text-blue-100 text-sm">
                Modify the technology stack, features, or requirements to match your specific needs. 
                The AI works best with detailed, specific instructions.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Start Simple</h4>
              <p className="text-blue-100 text-sm">
                Begin with basic features and gradually add complexity. 
                Use the iterative development approach described in the guide.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Use Agent Mode</h4>
              <p className="text-blue-100 text-sm">
                For complex projects, use Agent mode in VS Code. 
                It can handle multi-file operations and tool integrations automatically.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Follow Up</h4>
              <p className="text-blue-100 text-sm">
                After the initial setup, ask for specific improvements, 
                testing strategies, or additional features to enhance your project.
              </p>
            </div>
          </div>
        </div>

        {/* Get Started CTA */}
        <div className="mt-12 text-center">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Ready to Build Your Own Project?
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Use these examples as inspiration and start building with GitHub Copilot Agent today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="primary" 
              size="lg"
              onClick={() => window.location.href = '/quick-start'}
            >
              Start Quick Guide
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => window.location.href = '/guide'}
            >
              Read Full Guide
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Examples
